import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, signal } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TitleStrategy, provideRouter, withRouterConfig } from '@angular/router';
import { Configuration as ApiConfiguration, ApiModule } from '@enp/platform-api-client';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEvent, KeycloakEventType, provideKeycloak } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { Settings } from 'luxon';

import { Config } from '@shared//types';
import { provideNgxMask } from '@shared/directives/ngx-mask';
import { ngxMaskConfig } from '@shared/initializers';
import {
  blobInterceptor,
  impersonateAuthorizationHeaderInterceptor,
  tokenUpdateInterceptor,
  unauthorizedInterceptor,
} from '@shared/interceptors';
import { TitleService } from '@shared/services/title';
import { UserService } from '@shared/services/user';
import { CONFIG_TOKEN } from '@shared/tokens';
import { DEFAULT_LANG, DEFAULT_TIMEZONE, LOCALE_ID_VALUE } from '@shared/utils/locales';

import { BusinessProcessesEffects } from '@stores/business-processes';
import { CoreEffects } from '@stores/core';
import { CrisisEffects } from '@stores/crisis';
import { MessagesEffects } from '@stores/messages';
import { MetadataEffects } from '@stores/meta-data';
import { rootMetaReducers, rootReducer } from '@stores/root';

import { environment } from '@environments/environment';

import { MockKeycloak } from '../mocks/keycloak';
import { routes } from './app.routes';

Settings.defaultLocale = LOCALE_ID_VALUE;
Settings.defaultZone = DEFAULT_TIMEZONE;

export const appConfig = (config: Config): ApplicationConfig => ({
  providers: [
    { provide: CONFIG_TOKEN, useValue: config },
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(
      withInterceptors([tokenUpdateInterceptor, unauthorizedInterceptor, blobInterceptor, impersonateAuthorizationHeaderInterceptor])
    ),
    provideStore(rootReducer, {
      metaReducers: rootMetaReducers,
      runtimeChecks: {
        strictStateSerializability: false,
        // TODO: Luxon objects are not serializable
        strictActionSerializability: false,
        strictActionWithinNgZone: false,
      },
    }),
    provideEffects([CoreEffects, CrisisEffects, BusinessProcessesEffects, MessagesEffects, MetadataEffects]),
    provideRouterStore(),
    !environment.production ? provideStoreDevtools() : [],
    { provide: TitleStrategy, useClass: TitleService },
    provideAnimations(),
    { provide: LOCALE_ID, useValue: LOCALE_ID_VALUE },

    importProvidersFrom(ApiModule),
    {
      provide: ApiConfiguration,
      deps: [CONFIG_TOKEN, UserService],
      useFactory: (config: Config, userService: UserService) =>
        new ApiConfiguration({
          basePath: config.backendRootUrl,
          credentials: {
            AuthToken: () => userService.accessToken,
          },
        }),
    },

    environment.enableLogin
      ? provideKeycloak({
          config: {
            url: config.authUrl,
            realm: config.authRealm,
            clientId: config.authClientId,
          },
          initOptions: {
            pkceMethod: 'S256',
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri: `${window.location.origin}/assets/keycloak/silent-check-sso.html`,
          },
        })
      : [
          {
            provide: Keycloak,
            useClass: MockKeycloak,
          },
          {
            provide: KEYCLOAK_EVENT_SIGNAL,
            useValue: signal<KeycloakEvent>({
              type: KeycloakEventType.Ready,
            }),
          },
        ],

    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        useHttpBackend: true,
      }),
      fallbackLang: DEFAULT_LANG,
      lang: DEFAULT_LANG,
    }),

    provideNgxMask(ngxMaskConfig),
    provideSvgIconsConfig({
      sizes: {
        md: '16px',
        lg: '24px',
        xl: '40px',
        xxl: '56px',
      },
      defaultSize: 'lg',
    }),
  ],
});
